/**
 * 보안 관련 상수 정의
 * 모든 보안 설정을 중앙에서 관리합니다.
 */

// 패스워드 문자 세트
export const CHAR_SETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?',
  symbols: '~`!@#$%^&*()_+-={}[]|\\:";\'<>?,./',
  similar: 'il1Lo0O',
  ambiguous: '{}[]()/\\\'"`~,;.<>',
  pronounceableConsonants: 'bcdfghjklmnpqrstvwxyz',
  pronounceableVowels: 'aeiou'
};

// 패스워드 설정
export const PASSWORD_CONFIG = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  DEFAULT_LENGTH: 16,
  RECOMMENDED_MIN_LENGTH: 12,
  
  // 강도 임계값
  STRENGTH_THRESHOLDS: {
    VERY_WEAK: 0,
    WEAK: 1,
    MODERATE: 2,
    STRONG: 3,
    VERY_STRONG: 4
  }
};

// 패스워드 히스토리 설정
export const HISTORY_CONFIG = {
  MAX_ENTRIES_PER_PASSWORD: 10,
  MAX_RECENT_PASSWORDS: 10,
  RETENTION_DAYS: 365,
  BACKUP_RETENTION_DAYS: 90
};

// 중복 감지 설정
export const DUPLICATE_DETECTION = {
  EXACT_MATCH_THRESHOLD: 1.0,
  SIMILAR_PASSWORD_THRESHOLD: 0.8,
  WEAK_PATTERN_THRESHOLD: 0.6,
  MIN_COMPARISON_LENGTH: 4
};

// 보안 등급 설정
export const SECURITY_GRADES = {
  A_PLUS: { min: 95, label: 'A+', color: '#28a745' },
  A: { min: 85, label: 'A', color: '#20c997' },
  B: { min: 75, label: 'B', color: '#ffc107' },
  C: { min: 65, label: 'C', color: '#fd7e14' },
  D: { min: 0, label: 'D', color: '#dc3545' }
};

// 강도 레벨 정의
export const STRENGTH_LEVELS = [
  { 
    label: '매우 약함', 
    color: '#dc3545', 
    bgColor: '#f8d7da',
    score: 0
  },
  { 
    label: '약함', 
    color: '#fd7e14', 
    bgColor: '#ffeaa0',
    score: 1
  },
  { 
    label: '보통', 
    color: '#ffc107', 
    bgColor: '#fff3cd',
    score: 2
  },
  { 
    label: '강함', 
    color: '#20c997', 
    bgColor: '#d1ecf1',
    score: 3
  },
  { 
    label: '매우 강함', 
    color: '#28a745', 
    bgColor: '#d4edda',
    score: 4
  }
];

// 2FA 설정
export const TWO_FACTOR_CONFIG = {
  BACKUP_CODES_COUNT: 8,
  BACKUP_CODE_LENGTH: 8,
  TOTP_WINDOW: 1, // 허용 시간 윈도우 (30초 단위)
  SECRET_KEY_LENGTH: 32
};

// 세션 설정
export const SESSION_CONFIG = {
  DEFAULT_TIMEOUT_MINUTES: 30,
  MAX_TIMEOUT_MINUTES: 480, // 8시간
  INACTIVITY_CHECK_INTERVAL: 60000, // 1분
  WARNING_BEFORE_LOGOUT_MINUTES: 5
};

// 암호화 설정
export const ENCRYPTION_CONFIG = {
  ALGORITHM: 'AES-GCM',
  KEY_LENGTH: 256,
  IV_LENGTH: 96,
  TAG_LENGTH: 128,
  SALT_LENGTH: 128,
  ITERATIONS: 100000 // PBKDF2 반복 횟수
};

// 접근성 설정
export const ACCESSIBILITY_CONFIG = {
  MIN_TOUCH_TARGET_SIZE: 44, // px
  MIN_FONT_SIZE_MOBILE: 16, // iOS 줌 방지
  HIGH_CONTRAST_THRESHOLD: 4.5, // WCAG AA 기준
  ANIMATION_DURATION_MS: 300
};

// 성능 설정
export const PERFORMANCE_CONFIG = {
  DEBOUNCE_DELAY_MS: 300,
  SEARCH_MIN_CHARACTERS: 2,
  PAGINATION_PAGE_SIZE: 50,
  MAX_CONCURRENT_ANALYSIS: 5
};

// 오류 메시지
export const SECURITY_ERRORS = {
  PASSWORD_TOO_SHORT: '패스워드가 너무 짧습니다.',
  PASSWORD_TOO_LONG: '패스워드가 너무 깁니다.',
  INVALID_CHARACTERS: '허용되지 않는 문자가 포함되어 있습니다.',
  DUPLICATE_DETECTED: '이미 사용 중인 패스워드입니다.',
  SIMILAR_DETECTED: '유사한 패스워드가 존재합니다.',
  ENCRYPTION_FAILED: '암호화에 실패했습니다.',
  DECRYPTION_FAILED: '복호화에 실패했습니다.',
  HISTORY_SAVE_FAILED: '히스토리 저장에 실패했습니다.'
};

// 성공 메시지
export const SECURITY_MESSAGES = {
  PASSWORD_GENERATED: '새 패스워드가 생성되었습니다.',
  PASSWORD_COPIED: '패스워드가 클립보드에 복사되었습니다.',
  HISTORY_SAVED: '패스워드 히스토리가 저장되었습니다.',
  BACKUP_COMPLETED: '백업이 완료되었습니다.',
  SECURITY_ANALYSIS_COMPLETE: '보안 분석이 완료되었습니다.'
};

// 기본 패스워드 생성 옵션
export const DEFAULT_GENERATOR_OPTIONS = {
  length: PASSWORD_CONFIG.DEFAULT_LENGTH,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSpecialChars: true,
  excludeSimilar: true,
  excludeAmbiguous: true,
  includeSymbols: false,
  pronounceable: false,
  noConsecutive: true
};

// 유효성 검사 규칙
export const VALIDATION_RULES = {
  password: {
    minLength: PASSWORD_CONFIG.MIN_LENGTH,
    maxLength: PASSWORD_CONFIG.MAX_LENGTH,
    requireUppercase: false,
    requireLowercase: false,
    requireNumbers: false,
    requireSpecialChars: false
  },
  
  username: {
    minLength: 3,
    maxLength: 50,
    allowedChars: /^[a-zA-Z0-9._-]+$/
  },
  
  website: {
    maxLength: 200,
    pattern: /^https?:\/\/.+/
  }
};